import React, { createContext, useContext, useEffect, useState } from 'react'

import { Script } from 'gatsby'
import {
  SmarticoLoginError,
  SmarticoScriptLoadError,
  SmarticoStartupError,
  SmarticoUpdatePushNotificationTokenError,
} from '../errors/smartico'
import storageService from '../services/storageService'
import {
  init as initSmartico,
  login as smarticoLogin,
  logout as smarticoLogout,
  smarticoURL,
  updatePushNotificationToken,
} from '../utils/smarticoUtils'
import { Logger } from '../utils/loggerUtils'

const logger = new Logger.Builder()
  .setContext('Smartico')
  .enableSentryIntegration()
  .build()

const defaultValues = {
  isLoading: false,
  isLoggedIn: false,
  login: () => {},
  logout: () => {},
}

const SmarticoContext = createContext(defaultValues)

export const SmarticoProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [pendingLogin, setPendingLogin] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const init = () => {
    try {
      initSmartico()
    } catch (error) {
      logger.error(new SmarticoStartupError(error))
    }
  }

  const onLoad = () => {
    setIsLoading(false)
    init()
  }

  const onError = (error) => {
    logger.error(new SmarticoScriptLoadError(error))

    setLoadError(true)
    setIsLoading(false)
  }

  const addPendingLogin = () => {
    setPendingLogin(true)
  }

  const removePendingLogin = () => {
    setPendingLogin(false)
  }

  const login = async () => {
    const user = storageService.getUser()

    if (loadError) {
      logger.error(new SmarticoLoginError(user.id, loadError))

      removePendingLogin()
      return
    }

    if (isLoading) {
      addPendingLogin()
      return
    }

    try {
      await smarticoLogin(user)
      setIsLoggedIn(true)
    } catch (error) {
      logger.error(new SmarticoLoginError(user.id, loadError, error))
    } finally {
      removePendingLogin()
    }
  }

  const logout = () => {
    smarticoLogout()
    setIsLoggedIn(false)
  }

  const updateToken = () => {
    try {
      updatePushNotificationToken()
    } catch (error) {
      logger.error(new SmarticoUpdatePushNotificationTokenError(error))
    }
  }

  const retryLogin = () => {
    if (!pendingLogin) return

    login()
  }

  useEffect(() => {
    if (isLoading || isLoggedIn || loadError) return

    retryLogin()
  }, [isLoggedIn, isLoading, loadError, pendingLogin])

  const value = {
    isLoading,
    isLoggedIn,
    login,
    logout,
    updateToken,
  }

  return (
    <SmarticoContext.Provider value={value}>
      <Script src={smarticoURL} onLoad={onLoad} onError={onError} />

      {children}
    </SmarticoContext.Provider>
  )
}

export const useSmartico = () => {
  return useContext(SmarticoContext)
}
